/**
 * Phase 9.7.6 — 💰 Live Earnings Panel
 * Phase 14.3 — Live gift visibility upgrade:
 *   • Pulses + glows on every incoming gift
 *   • Floats a "+coins" bubble next to the Coins stat
 *   • Surfaces the host's wallet balance live (host-only) so they
 *     literally watch their earnings tick up in real time
 */
import { useEffect, useRef, useState } from "react";

const fmt = (n) => Number(n || 0).toLocaleString();
// Mirrors backend payout_routes COINS_TO_USD (0.0075). Display only —
// not authoritative for actual payout calculation.
const COINS_TO_USD = 0.0075;

const Stat = ({ icon, label, value, sub, testid, highlight = false, children }) => (
  <div
    data-testid={testid}
    className={`relative flex flex-col items-center justify-center px-2 py-2 rounded-lg border transition-all ${
      highlight
        ? "bg-amber-400/15 border-amber-300/60 shadow-[0_0_18px_rgba(251,191,36,0.45)]"
        : "bg-black/30 border-amber-500/20"
    }`}
  >
    <span className="text-base">{icon}</span>
    <span className="text-white font-black text-sm leading-none">{value}</span>
    <span className="text-[9px] uppercase tracking-wider text-amber-200/70 font-bold mt-0.5">
      {label}
    </span>
    {sub && <span className="text-[9px] text-pink-200/60 mt-0.5">{sub}</span>}
    {children}
  </div>
);

const LiveEarningsPanel = ({
  room,
  leaderboard = [],
  recentGiftCount = 0,
  pulse = null,
  isHost = false,
  hostWalletBalance = null,
}) => {
  // Track recent pulses so we can render up to 3 floaters stacked.
  const [floaters, setFloaters] = useState([]);
  const [panelGlow, setPanelGlow] = useState(false);
  const lastPulseIdRef = useRef(null);

  useEffect(() => {
    if (!pulse || !pulse.id || pulse.id === lastPulseIdRef.current) return;
    lastPulseIdRef.current = pulse.id;
    const f = { ...pulse };
    setFloaters((cur) => [...cur, f].slice(-3));
    setPanelGlow(true);
    const t1 = setTimeout(() => {
      setFloaters((cur) => cur.filter((x) => x.id !== f.id));
    }, 1800);
    const t2 = setTimeout(() => setPanelGlow(false), 1100);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [pulse]);

  if (!room) return null;
  const totalCoins = Number(room.gift_total_coins || 0);
  const usd = totalCoins * COINS_TO_USD;
  const top = (leaderboard || [])[0];
  const hostName = room.host_name || "the host";

  return (
    <div
      data-testid="live-earnings-panel"
      className={`relative rounded-xl border bg-gradient-to-br from-amber-500/10 via-pink-500/5 to-purple-500/5 p-3 transition-all ${
        panelGlow
          ? "border-amber-300/80 shadow-[0_0_28px_rgba(251,191,36,0.55)] scale-[1.015]"
          : "border-amber-400/30"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-amber-200 font-black flex items-center gap-1.5">
          <span>💰 Beam Earnings</span>
          {panelGlow && (
            <span
              className="inline-flex items-center gap-1 text-[9px] tracking-widest text-amber-100 bg-amber-500/30 border border-amber-300/60 rounded-full px-1.5 py-0.5 animate-pulse"
              data-testid="live-earnings-live-pill"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-200 animate-pulse" />
              LIVE
            </span>
          )}
        </p>
        <span
          className="text-[10px] text-pink-200/70"
          data-testid="live-earnings-recipient"
        >
          → {hostName}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat
          icon="🪙"
          label="Coins"
          value={fmt(totalCoins)}
          testid="live-earnings-coins"
          highlight={panelGlow}
        >
          {/* Floating +coins bubbles — stack up to 3 */}
          <div className="pointer-events-none absolute -top-1 right-1 flex flex-col items-end gap-0.5">
            {floaters.map((f, i) => (
              <span
                key={f.id}
                data-testid="live-earnings-float"
                className="text-[11px] font-black text-amber-200 drop-shadow-[0_0_8px_rgba(251,191,36,0.9)]"
                style={{
                  animation: "earnings-floater-rise 1700ms ease-out forwards",
                  animationDelay: `${i * 80}ms`,
                }}
              >
                {f.emoji || "🎁"} +{fmt(f.coins)}
              </span>
            ))}
          </div>
        </Stat>
        <Stat
          icon="💎"
          label="USD"
          value={`$${usd.toFixed(2)}`}
          sub="est."
          testid="live-earnings-diamonds"
        />
        <Stat
          icon="🎁"
          label="Gifts"
          value={fmt(recentGiftCount)}
          testid="live-earnings-gift-count"
          highlight={panelGlow}
        />
        <Stat
          icon="👑"
          label="Top"
          value={top ? (top.name || "—").split(" ")[0].slice(0, 8) : "—"}
          sub={top ? `${fmt(top.coins || 0)}c` : "no gifts yet"}
          testid="live-earnings-top-supporter"
        />
      </div>

      {/* Phase 14.3 — Host-only live wallet readout. The host's own
          coin balance keeps ticking up as gifts land in real time so
          they can see the money move. */}
      {isHost && hostWalletBalance !== null && hostWalletBalance !== undefined && (
        <div
          className="mt-2 flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-400/40"
          data-testid="live-earnings-host-wallet"
        >
          <span className="text-[10px] uppercase tracking-wider text-emerald-200/90 font-black">
            👛 Your Wallet
          </span>
          <span className="text-emerald-100 font-black text-sm">
            {fmt(hostWalletBalance)} coins
          </span>
        </div>
      )}

      <style>{`
        @keyframes earnings-floater-rise {
          0%   { opacity: 0; transform: translateY(6px) scale(0.85); }
          12%  { opacity: 1; transform: translateY(-2px) scale(1.05); }
          75%  { opacity: 1; transform: translateY(-28px) scale(1); }
          100% { opacity: 0; transform: translateY(-44px) scale(0.95); }
        }
      `}</style>
    </div>
  );
};

export default LiveEarningsPanel;
